import React, { useState, useEffect, useCallback } from "react";
import { useAtom } from "jotai";
import { themeAtom } from "../../../../-Atoms";
import { db, doc, updateDoc } from "../../../../firebaseConfig";
import { listSubfolders } from "../../../../DropboxFunctions";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";

import FolderIcon from "@mui/icons-material/Folder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import BlockIcon from "@mui/icons-material/Block";

// Default folders that are pre-supplied (visible before "Supplied" label)
const DEFAULT_VISIBLE_FOLDERS = [
    "/storyboards",
    "/storyboard selects",
    "/quickproofs",
    "/content",
    "/early supply",
];

/**
 * Three states for folder visibility:
 * - "default": Visible only after "Supplied" label is added
 * - "included": Always visible (in suppliedFolders)
 * - "excluded": Never visible (in excludedFolders)
 */
const STATES = {
    DEFAULT: "default",
    INCLUDED: "included",
    EXCLUDED: "excluded",
};

// Cycle through states: default -> included -> excluded -> default
const cycleState = (currentState) => {
    switch (currentState) {
        case STATES.DEFAULT:
            return STATES.INCLUDED;
        case STATES.INCLUDED:
            return STATES.EXCLUDED;
        case STATES.EXCLUDED:
            return STATES.DEFAULT;
        default:
            return STATES.DEFAULT;
    }
};

const FolderItem = ({
    folder,
    depth = 0,
    folderStates,
    onToggle,
    theme,
    expandedFolders,
    toggleExpanded,
    loadingFolders,
    onExpand,
}) => {
    const hasChildren = folder.children && folder.children.length > 0;
    const isExpanded = expandedFolders.has(folder.path);
    const isLoading = loadingFolders.has(folder.path);
    const state = folderStates[folder.path] || STATES.DEFAULT;

    const getStateIcon = () => {
        switch (state) {
            case STATES.INCLUDED:
                return <CheckBoxIcon sx={{ color: "#4caf50" }} />;
            case STATES.EXCLUDED:
                return <BlockIcon sx={{ color: "#f44336" }} />;
            default:
                return (
                    <CheckBoxOutlineBlankIcon
                        sx={{ color: theme === "dark" ? "#888" : "#666" }}
                    />
                );
        }
    };

    const getStateLabel = () => {
        switch (state) {
            case STATES.INCLUDED:
                return "Always visible";
            case STATES.EXCLUDED:
                return "Never visible";
            default:
                return "When Supplied";
        }
    };

    const handleExpandClick = async () => {
        if (!isExpanded && !folder.childrenLoaded) {
            await onExpand(folder.path);
        }
        toggleExpanded(folder.path);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 0.75,
                    px: 1,
                    ml: depth * 2,
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": {
                        backgroundColor:
                            theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                    },
                }}
            >
                {/* Expand/Collapse button */}
                <IconButton
                    size="small"
                    onClick={handleExpandClick}
                    sx={{
                        p: 0.5,
                        visibility: hasChildren ? "visible" : "hidden",
                    }}
                >
                    {isLoading ? (
                        <CircularProgress size={16} />
                    ) : isExpanded ? (
                        <ExpandMoreIcon fontSize="small" />
                    ) : (
                        <ChevronRightIcon fontSize="small" />
                    )}
                </IconButton>

                {/* Folder icon */}
                <FolderIcon
                    sx={{
                        mr: 1,
                        color:
                            state === STATES.INCLUDED
                                ? "#4caf50"
                                : state === STATES.EXCLUDED
                                    ? "#f44336"
                                    : theme === "dark"
                                        ? "#90caf9"
                                        : "#1976d2",
                        fontSize: 20,
                    }}
                />

                {/* Folder name */}
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: 14,
                        fontWeight: 500,
                        color:
                            state === STATES.EXCLUDED
                                ? theme === "dark"
                                    ? "#888"
                                    : "#999"
                                : undefined,
                        textDecoration: state === STATES.EXCLUDED ? "line-through" : "none",
                    }}
                >
                    {folder.name}
                </Typography>

                {/* State label */}
                <Typography
                    sx={{
                        fontSize: 11,
                        color: theme === "dark" ? "#888" : "#666",
                        mr: 1,
                    }}
                >
                    {getStateLabel()}
                </Typography>

                {/* State toggle button */}
                <IconButton size="small" onClick={() => onToggle(folder.path)} sx={{ p: 0.5 }}>
                    {getStateIcon()}
                </IconButton>
            </Box>

            {/* Children */}
            <Collapse in={isExpanded}>
                {folder.children &&
                    folder.children.map((child) => (
                        <FolderItem
                            key={child.path}
                            folder={child}
                            depth={depth + 1}
                            folderStates={folderStates}
                            onToggle={onToggle}
                            theme={theme}
                            expandedFolders={expandedFolders}
                            toggleExpanded={toggleExpanded}
                            loadingFolders={loadingFolders}
                            onExpand={onExpand}
                        />
                    ))}
            </Collapse>
        </Box>
    );
};

const SuppliedFoldersDialog = ({ open, onClose, jobData }) => {
    const [theme] = useAtom(themeAtom);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [folders, setFolders] = useState([]);
    const [folderStates, setFolderStates] = useState({});
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const [loadingFolders, setLoadingFolders] = useState(new Set());
    const [error, setError] = useState(null);

    // Build the base Dropbox path for this job (always root, never day-specific)
    // Supply folder lives at the root job path regardless of multipleDays
    const getBasePath = useCallback(() => {
        if (!jobData) return null;
        const jobName = `${jobData.jobNumber}-${jobData.client}${jobData.jobTypeShort || ""}`;
        return `/${jobData.client}/${jobName}`;
    }, [jobData]);

    // Initialize folder states from job data
    useEffect(() => {
        if (!open || !jobData) return;

        const visibleFolders = jobData.visibleFolders || DEFAULT_VISIBLE_FOLDERS;
        const excludedFolders = jobData.excludedFolders || [];

        const states = {};
        visibleFolders.forEach((path) => {
            states[path.toLowerCase()] = STATES.INCLUDED;
        });
        excludedFolders.forEach((path) => {
            states[path.toLowerCase()] = STATES.EXCLUDED;
        });

        setFolderStates(states);
    }, [open, jobData]);

    // Load all folders recursively when dialog opens
    useEffect(() => {
        if (!open || !jobData) return;

        const loadAllFolders = async () => {
            setLoading(true);
            setError(null);

            try {
                const basePath = getBasePath();
                if (!basePath) {
                    setError("Could not determine job folder path");
                    setLoading(false);
                    return;
                }

                // Recursive function to load a folder and all its children
                const loadFolderRecursively = async (fullPath, relativePath, depth = 0) => {
                    const MAX_DEPTH = 3; // Limit recursion depth

                    try {
                        const result = await listSubfolders(fullPath);
                        const children = await Promise.all(
                            result.entries.map(async (entry) => {
                                const childRelativePath = `${relativePath}/${entry.name.toLowerCase()}`;
                                const childFullPath = entry.path_lower;

                                // Recursively load children if within depth limit
                                let grandChildren = [];
                                if (depth < MAX_DEPTH) {
                                    grandChildren = await loadFolderRecursively(
                                        childFullPath,
                                        childRelativePath,
                                        depth + 1
                                    );
                                }

                                return {
                                    name: entry.name,
                                    path: childRelativePath,
                                    fullPath: childFullPath,
                                    children: grandChildren,
                                    childrenLoaded: depth < MAX_DEPTH,
                                };
                            })
                        );

                        // Sort children alphabetically
                        children.sort((a, b) => a.name.localeCompare(b.name));
                        return children;
                    } catch (err) {
                        console.warn(`Error loading subfolders for ${fullPath}:`, err);
                        return [];
                    }
                };

                // Load root folders and all their children
                const rootFolders = await loadFolderRecursively(basePath, "");
                setFolders(rootFolders);

                // Auto-expand folders with children
                const expanded = new Set();
                const collectFoldersWithChildren = (items, depth = 0) => {
                    items.forEach(item => {
                        if (item.children && item.children.length > 0 && depth < 1) {
                            expanded.add(item.path);
                            collectFoldersWithChildren(item.children, depth + 1);
                        }
                    });
                };
                collectFoldersWithChildren(rootFolders);
                setExpandedFolders(expanded);

            } catch (err) {
                console.error("Error loading folders:", err);
                setError("Failed to load folders from Dropbox");
            } finally {
                setLoading(false);
            }
        };

        loadAllFolders();
    }, [open, jobData, getBasePath]);

    // Load subfolders for a specific path
    const loadSubfolders = async (folderPath) => {
        const basePath = getBasePath();
        if (!basePath) return;

        // Find the folder in our tree and get its full Dropbox path
        const findFolder = (items, targetPath) => {
            for (const item of items) {
                if (item.path === targetPath) return item;
                if (item.children) {
                    const found = findFolder(item.children, targetPath);
                    if (found) return found;
                }
            }
            return null;
        };

        const folder = findFolder(folders, folderPath);
        if (!folder || folder.childrenLoaded) return;

        setLoadingFolders((prev) => new Set([...prev, folderPath]));

        try {
            const result = await listSubfolders(folder.fullPath);
            const children = result.entries.map((entry) => ({
                name: entry.name,
                path: `${folderPath}/${entry.name.toLowerCase()}`,
                fullPath: entry.path_lower,
                children: [],
                childrenLoaded: false,
            }));

            children.sort((a, b) => a.name.localeCompare(b.name));

            // Update the folder tree
            setFolders((prev) => {
                const updateTree = (items) =>
                    items.map((item) => {
                        if (item.path === folderPath) {
                            return { ...item, children, childrenLoaded: true };
                        }
                        if (item.children) {
                            return { ...item, children: updateTree(item.children) };
                        }
                        return item;
                    });
                return updateTree(prev);
            });
        } catch (err) {
            console.error("Error loading subfolders:", err);
        } finally {
            setLoadingFolders((prev) => {
                const next = new Set(prev);
                next.delete(folderPath);
                return next;
            });
        }
    };

    const toggleExpanded = (path) => {
        setExpandedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(path)) {
                next.delete(path);
            } else {
                next.add(path);
            }
            return next;
        });
    };

    const handleToggleState = (path) => {
        setFolderStates((prev) => {
            const currentState = prev[path] || STATES.DEFAULT;
            const newState = cycleState(currentState);

            // Create new states object
            const newStates = { ...prev };

            // Update the target folder
            if (newState === STATES.DEFAULT) {
                delete newStates[path];
            } else {
                newStates[path] = newState;
            }

            // CASCADE DOWN: Update all subfolders to match the parent's new state
            // We can identify subfolders because they start with the parent path + "/"
            // Traverse the `folders` tree to find strict children isn't needed if we use path matching
            // But we only want to update known/loaded subfolders that are currently in the `folders` structure
            // However, iterating the `prev` state is easier if we only affect overridden folders
            // Actually, we want to update ALL known subfolders (loaded ones)

            // Helper to collect all known descendents of a path from the folders tree
            const collectDescendents = (items, parentPath, resultSet) => {
                items.forEach(item => {
                    if (item.path.startsWith(parentPath + "/") || item.path === parentPath) {
                        if (item.path !== parentPath) resultSet.add(item.path);
                        if (item.children) {
                            collectDescendents(item.children, parentPath, resultSet);
                        }
                    } else if (item.children) {
                        // Traverse deeper even if this node doesn't match (it shouldn't happen if tree is sound, but good for safety)
                        collectDescendents(item.children, parentPath, resultSet);
                    }
                });
            };

            const descendents = new Set();
            collectDescendents(folders, path, descendents);

            descendents.forEach(childPath => {
                if (newState === STATES.DEFAULT) {
                    delete newStates[childPath];
                } else {
                    newStates[childPath] = newState;
                }
            });

            return newStates;
        });
    };

    const handleSave = async () => {
        if (!jobData?.id) return;

        setSaving(true);

        try {
            const visibleFolders = [];
            const excludedFolders = [];

            Object.entries(folderStates).forEach(([path, state]) => {
                if (state === STATES.INCLUDED) {
                    visibleFolders.push(path);
                } else if (state === STATES.EXCLUDED) {
                    excludedFolders.push(path);
                }
            });

            await updateDoc(doc(db, "JobData", jobData.id), {
                visibleFolders,
                excludedFolders,
            });

            onClose();
        } catch (err) {
            console.error("Error saving folder configuration:", err);
            setError("Failed to save configuration");
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: theme === "dark" ? "#1e1e1e" : "#fff",
                    backgroundImage: "none",
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
                        }`,
                }}
            >
                Change Supply Folder Visibility
            </DialogTitle>

            <DialogContent sx={{ p: 0, minHeight: 300 }}>
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: 300,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: 300,
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        <Typography color="error">{error}</Typography>
                        <Button onClick={() => setError(null)}>Retry</Button>
                    </Box>
                ) : folders.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: 300,
                        }}
                    >
                        <Typography color="textSecondary">No folders found</Typography>
                    </Box>
                ) : (
                    <Box sx={{ py: 1 }}>
                        {/* Legend */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 3,
                                px: 2,
                                py: 1,
                                mb: 1,
                                borderBottom: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
                                    }`,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <CheckBoxOutlineBlankIcon sx={{ fontSize: 18, color: "#888" }} />
                                <Typography sx={{ fontSize: 12, color: "#888" }}>
                                    When Supplied
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <CheckBoxIcon sx={{ fontSize: 18, color: "#4caf50" }} />
                                <Typography sx={{ fontSize: 12, color: "#888" }}>
                                    Always Visible
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <BlockIcon sx={{ fontSize: 18, color: "#f44336" }} />
                                <Typography sx={{ fontSize: 12, color: "#888" }}>
                                    Never Visible
                                </Typography>
                            </Box>
                        </Box>

                        {/* Folder tree */}
                        <Box sx={{ px: 1 }}>
                            {folders.map((folder) => (
                                <FolderItem
                                    key={folder.path}
                                    folder={folder}
                                    folderStates={folderStates}
                                    onToggle={handleToggleState}
                                    theme={theme}
                                    expandedFolders={expandedFolders}
                                    toggleExpanded={toggleExpanded}
                                    loadingFolders={loadingFolders}
                                    onExpand={loadSubfolders}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
                        }`,
                    px: 2,
                    py: 1.5,
                }}
            >
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || loading}
                >
                    {saving ? <CircularProgress size={20} /> : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SuppliedFoldersDialog;
